import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { esquemaRegistro } from '@compranorte/contracts'
import { useRegistrar } from '../api/hooks'
import { MarcoAcceso } from '../componentes/MarcoAcceso'
import { IconoCheck } from '../componentes/icons'
import { Aviso } from '../componentes/ui'

/** El mínimo real lo fija `esquemaClave` en los contratos; acá solo se enuncia. */
const MINIMO_CLAVE = 12

export function Registro() {
  const registrar = useRegistrar()
  const [nombre, setNombre] = useState('')
  const [email, setEmail] = useState('')
  const [clave, setClave] = useState('')
  const [repetir, setRepetir] = useState('')
  const [campos, setCampos] = useState<Record<string, string>>({})

  function enviar(ev: FormEvent) {
    ev.preventDefault()
    if (registrar.isPending) return

    const revision = esquemaRegistro.safeParse({ nombre, email, clave })
    const errores: Record<string, string> = {}
    if (!revision.success) {
      for (const i of revision.error.issues) {
        const campo = i.path[0]
        if (typeof campo === 'string' && !errores[campo]) errores[campo] = i.message
      }
    }
    /*
     * La repetición no está en el contrato porque al servidor no le importa: es
     * una salvaguarda contra el error de tecleo, y vive solo acá.
     */
    if (clave && clave !== repetir) errores['repetir'] = 'Las contraseñas no coinciden.'

    if (Object.keys(errores).length > 0 || !revision.success) {
      setCampos(errores)
      return
    }

    setCampos({})
    registrar.mutate(revision.data)
  }

  const enVuelo = registrar.isPending
  /* Los errores por campo que manda el backend (clave débil, sobre todo) se
   * muestran junto al campo; el resto, arriba. */
  const errorApi = registrar.error
  const errorClaveApi = errorApi?.detalleDe('clave')

  if (registrar.isSuccess) {
    return (
      <MarcoAcceso ancho="max-w-[420px]">
        <h1 className="text-[17px] font-semibold text-acero-900 flex items-center gap-2">
          <IconoCheck size={18} className="text-ok-500" />
          Cuenta creada
        </h1>

        <div className="mt-3 space-y-3">
          <Aviso tono="ok" titulo="Revise su correo">
            {registrar.data.mensaje}
          </Aviso>
          <Aviso tono="info" titulo="Falta que le asignemos su acceso">
            La cuenta queda sin permisos hasta que un administrador de Compranorte indique si
            usted es <strong>cliente</strong> o <strong>proveedor</strong> y la asocie a su
            empresa. Ese paso solo se puede hacer con el correo ya confirmado, así que confirme
            primero. Mientras tanto puede recorrer el catálogo, pero no comprar ni publicar.
          </Aviso>
        </div>

        <div className="mt-4 flex gap-2">
          <Link to="/entrar" className="btn btn-accion">
            Entrar
          </Link>
          <Link to="/catalogo" className="btn btn-secundario">
            Ver el catálogo
          </Link>
        </div>
      </MarcoAcceso>
    )
  }

  return (
    <MarcoAcceso
      ancho="max-w-[420px]"
      pie={
        <Link to="/catalogo" className="text-[12px] text-acero-500 hover:text-acero-800">
          ← Seguir mirando el catálogo
        </Link>
      }
    >
      <h1 className="text-[17px] font-semibold text-acero-900">Crear cuenta</h1>
      <p className="text-[12.5px] text-acero-600 mt-1 leading-relaxed">
        Para comprar como cliente o para publicar productos como proveedor.
      </p>

      <form onSubmit={enviar} noValidate className="mt-4 space-y-3">
        {errorApi && !errorClaveApi && (
          <Aviso tono="error">
            <span role="alert">{errorApi.message}</span>
            {errorApi.codigo === 'conflicto' && (
              <>
                {' '}
                <Link to="/entrar" className="text-marca-600 underline hover:no-underline">
                  Ir a entrar
                </Link>
                {' o '}
                <Link to="/recuperar" className="text-marca-600 underline hover:no-underline">
                  recuperar la contraseña
                </Link>
                .
              </>
            )}
          </Aviso>
        )}

        <label className="block">
          <span className="etiqueta">Nombre y apellido</span>
          <input
            className={`campo ${campos['nombre'] ? 'campo-error' : ''}`}
            autoComplete="name"
            autoFocus
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            disabled={enVuelo}
          />
          {campos['nombre'] && <p className="error-msg">{campos['nombre']}</p>}
        </label>

        <label className="block">
          <span className="etiqueta">Correo</span>
          <input
            className={`campo ${campos['email'] ? 'campo-error' : ''}`}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={enVuelo}
          />
          {campos['email'] ? (
            <p className="error-msg">{campos['email']}</p>
          ) : (
            <p className="ayuda">
              Le enviaremos un enlace para confirmarlo. Hasta que lo confirme, un administrador no
              puede asignarle acceso.
            </p>
          )}
        </label>

        <label className="block">
          <span className="etiqueta">Contraseña</span>
          <input
            className={`campo ${campos['clave'] || errorClaveApi ? 'campo-error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            disabled={enVuelo}
          />
          {campos['clave'] || errorClaveApi ? (
            <p className="error-msg">{campos['clave'] ?? errorClaveApi}</p>
          ) : (
            <p className="ayuda">
              Mínimo {MINIMO_CLAVE} caracteres. No puede ser una contraseña conocida ni contener su
              correo o su nombre.
            </p>
          )}
        </label>

        <label className="block">
          <span className="etiqueta">Repita la contraseña</span>
          <input
            className={`campo ${campos['repetir'] ? 'campo-error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={repetir}
            onChange={(e) => setRepetir(e.target.value)}
            disabled={enVuelo}
          />
          {campos['repetir'] && <p className="error-msg">{campos['repetir']}</p>}
        </label>

        <button type="submit" className="btn btn-accion w-full" disabled={enVuelo}>
          {enVuelo ? 'Creando…' : 'Crear cuenta'}
        </button>
      </form>

      <p className="ayuda mt-3 text-center">
        ¿Ya tiene cuenta?{' '}
        <Link to="/entrar" className="text-marca-600 hover:underline">
          Entre
        </Link>
      </p>
    </MarcoAcceso>
  )
}
